/**
 * ProTip — small inline callout for in-context tips.
 *
 * Used throughout the app to highlight short "do this before that" or
 * "here's the easy way" advice. Visually distinct (amber/gold) so it
 * stands out without being alarming, and dismissable per-session so
 * power users aren't nagged forever.
 *
 * Each ProTip is keyed by `id` — once dismissed it stays dismissed in
 * sessionStorage so it returns on next reload (these are short tips,
 * a forever-dismiss feels too permanent for what they are).
 *
 * Usage:
 *   <ProTip id="bookings.appointment-types.order">
 *     Set up your availability and locations FIRST…
 *   </ProTip>
 */
import { useState } from "react";
import { Lightbulb, X } from "lucide-react";

const SS_PREFIX = "bloq.protip.dismissed.";

export default function ProTip({ id, children, className = "" }) {
  const storageKey = id ? `${SS_PREFIX}${id}` : null;
  const [dismissed, setDismissed] = useState(() => {
    if (!storageKey) return false;
    try { return sessionStorage.getItem(storageKey) === "1"; }
    catch { return false; }
  });
  if (dismissed) return null;
  const onDismiss = () => {
    if (storageKey) {
      try { sessionStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    }
    setDismissed(true);
  };
  return (
    <div
      className={`bloq-card p-3 border-l-4 border-l-[#b45309] bg-[#fff5e6] flex items-start gap-2.5 ${className}`}
      data-testid={id ? `pro-tip-${id}` : "pro-tip"}
    >
      <Lightbulb className="w-4 h-4 text-[#b45309] flex-shrink-0 mt-0.5" />
      <p className="flex-1 text-xs sm:text-sm text-[#7c3a06] leading-relaxed">
        <span className="font-semibold uppercase tracking-wider text-[10px] mr-1.5 text-[#b45309]">
          Pro tip
        </span>
        {children}
      </p>
      <button
        type="button"
        onClick={onDismiss}
        className="w-6 h-6 grid place-items-center hover:bg-[rgba(180,83,9,0.12)] rounded flex-shrink-0"
        aria-label="Dismiss pro tip"
        data-testid={id ? `pro-tip-${id}-dismiss` : "pro-tip-dismiss"}
      >
        <X className="w-3.5 h-3.5 text-[#7c3a06]" />
      </button>
    </div>
  );
}
